'use client';

import { useRouter } from '@bprogress/next/app';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import type { CookieConsentGroups } from '../../types';
import { DEFAULT_COOKIE_GROUPS } from '../../utils/cookieConsent';
import {
  acceptAllCookieConsent,
  saveCookieConsentGroups
} from '../../utils/cookieConsentActions';
import {
  dismissCookieConsentForSession,
  pushCookieConsentChoice
} from '../../utils/cookieConsentEvents';
import { Banner } from '../Banner/Banner';
import { Settings } from '../Settings/Settings';

export interface ConsentProps {
  open?: boolean;
}

export function Consent({ open = true }: ConsentProps) {
  const router = useRouter();
  const [bannerOpen, setBannerOpen] = useState(open);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [groups, setGroups] = useState<CookieConsentGroups>(
    DEFAULT_COOKIE_GROUPS
  );

  const pathname = usePathname();
  const isCookiePolicyPage = pathname === '/legal/politika-cookie/';

  if (isCookiePolicyPage) return;

  const closeAll = () => {
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const persist = (
    nextGroups: CookieConsentGroups,
    action: () => Promise<void>
  ) => {
    pushCookieConsentChoice(nextGroups);
    closeAll();
    void action().then(() => router.refresh());
  };

  const onAcceptAll = () => {
    persist(DEFAULT_COOKIE_GROUPS, acceptAllCookieConsent);
  };

  const onClose = () => {
    setBannerOpen(false);
  };

  const onDismiss = () => {
    dismissCookieConsentForSession();
    closeAll();
  };

  const onConfigure = () => {
    setGroups(DEFAULT_COOKIE_GROUPS);
    setBannerOpen(false);
    setSettingsOpen(true);
  };

  const onSaveSettings = () => {
    persist(groups, () => saveCookieConsentGroups(groups));
  };

  const onCloseSettings = () => {
    setSettingsOpen(false);
    setBannerOpen(true);
  };

  if (!bannerOpen && !settingsOpen) return null;

  return (
    <>
      {bannerOpen && (
        <Banner
          onAcceptAll={onAcceptAll}
          onConfigure={onConfigure}
          onDismiss={onDismiss}
          onClose={onClose}
        />
      )}
      <Settings
        open={settingsOpen}
        groups={groups}
        onGroupsChange={setGroups}
        onSave={onSaveSettings}
        onCancel={onCloseSettings}
        onClose={onCloseSettings}
      />
    </>
  );
}
